import {
  Column,
  Columns,
  FlexBox,
  Icon,
  Modal,
  ModalBody,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useRef } from 'react';
import { useUnmount } from 'react-use';

import { appActions } from 'app/app.actions';
import {
  CommandLauncherHint,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './commandLauncher.module.scss';
import { ConversationScopeList } from './components/conversationScopeList/conversationScopeList';
import { MacroScopeList } from './components/macroScopeList/macroScopeList';
import { MainSelectionList } from './components/mainScopeList/mainScopeList';
import { UserScopeList } from './components/userScopeList/userScopeList';
import { useRegisterDefaultActions } from './hooks/useRegisterDefaultActions';

/** Converts CommandLauncherScope to a string for tracking. */
const getScopeName = (scope: CommandLauncherScope): string => {
  switch (scope) {
    case CommandLauncherScope.User:
      return 'user';
    case CommandLauncherScope.Conversation:
      return 'conversation';
    case CommandLauncherScope.Macro:
      return 'macro';
    default:
      return 'main';
  }
};

const defaultHints: readonly CommandLauncherHint[] = [
  { keys: '↑↓', label: 'Navigate' },
  { keys: '↵', label: 'Select' },
  { keys: 'esc', label: 'Close' },
];

/**
 * Represents the component for displaying a command launcher that can be
 * triggered with a hotkey.
 */
export const CommandLauncher = createAppModal(
  { type: 'app:commandLauncher', noGhostPanel: true },
  ({ isOpen, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const {
      asyncStatus,
      query,
      scope,
      hints,
      isInputReadOnly,
      setIsActive,
      setQuery,
    } = useCommandLauncher();
    const inputRef = useRef<HTMLInputElement>(null);

    // Register default navigation actions
    useRegisterDefaultActions();

    // Handles closing the command launcher (via ESC or click outside)
    const handleClose = useCallback(() => {
      ampli.commandPaletteClosed({
        closedBy: 'escape', // Modal handles both ESC and click outside
        query: query || undefined,
        scope: getScopeName(scope ?? CommandLauncherScope.Main),
      });
      setQuery('');
      hide();
    }, [query, scope, hide, setQuery]);

    useUnmount(() => setQuery(''));

    // Focus input when query is programmatically set (e.g., @, #, or !)
    useEffect(() => {
      if (isOpen && (query === '@' || query === '#' || query === '!')) {
        inputRef.current?.focus();
      }
    }, [isOpen, query]);

    // Opens the feedback modal
    const handleFeedbackClick = useCallback(() => {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:feedbackForm',
            arg: {
              title: 'Command Launcher feedback',
              team: 'conversations',
              placeholder:
                'Tell us what you think about the Command Launcher (Cmd+K).',
            },
          },
        }),
      );
      hide();
    }, [dispatch, hide]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        allowClickOutside
        allowEscClose
        skipAnimation
        className={styles.modal}
      >
        <ModalBody className={styles.modalBody}>
          <Columns className={styles.root}>
            <Column>
              <Column.Row noMinHeight borderBottom>
                <FlexBox alignItems="center" gap="250">
                  {asyncStatus === 'pending' ? (
                    <Spinner size={20} />
                  ) : (
                    <Icon name="search" size={20} />
                  )}

                  <input
                    ref={inputRef}
                    className={styles.input}
                    data-command-launcher-input
                    autoFocus
                    placeholder="Search commands, @ contacts, # conversations, ! macros..."
                    value={query}
                    readOnly={isInputReadOnly}
                    onFocus={() => setIsActive(true)}
                    onBlur={() => setIsActive(false)}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </FlexBox>
              </Column.Row>

              <Column.Row
                scrollable
                noMinHeight
                noHorizontalPadding
                noVerticalPadding
                className={classNames({
                  [styles.results]: scope !== CommandLauncherScope.Conversation,
                })}
              >
                {scope === CommandLauncherScope.Main && <MainSelectionList />}

                {scope === CommandLauncherScope.Conversation && (
                  <ConversationScopeList />
                )}

                {scope === CommandLauncherScope.User && <UserScopeList />}

                {scope === CommandLauncherScope.Macro && <MacroScopeList />}
              </Column.Row>

              <Column.Row
                slottable
                borderTop
                noMinHeight
                className={styles.hints}
              >
                <Column.Slot placement="start">
                  <FlexBox alignItems="center" gap="400">
                    {(hints ?? defaultHints).map(({ keys, label }) => (
                      <span key={keys}>
                        <kbd>{keys}</kbd> {label}
                      </span>
                    ))}
                  </FlexBox>
                </Column.Slot>

                <Column.Slot placement="end">
                  <Text.ExternalLink
                    onClick={handleFeedbackClick}
                    fontSize="200"
                    color="secondary"
                  >
                    Send feedback
                  </Text.ExternalLink>
                </Column.Slot>
              </Column.Row>
            </Column>
          </Columns>
        </ModalBody>
      </Modal>
    );
  },
);
